import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, TableShell } from "@/components/EntityManager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Printer, Search, Building2, Trash2, Pencil, Paperclip, FileSpreadsheet, MessageCircle, StickyNote } from "lucide-react";
import { inr, fmtDate } from "@/lib/format";
import { printInvoice, printInvoices, printOutstandingReport, printNoteVoucher } from "@/lib/print";
import Gstr1Dialog from "@/components/Gstr1Dialog";
import { ListPagination, NewBadge } from "@/components/ListPagination";
import { PresetChips } from "@/pages/BillCreate";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

import { BRANCHES as OFFICES } from "@/lib/branches";

export default function Bills() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [billJump, setBillJump] = useState("");
  const [billHl, setBillHl] = useState(null);
  const [officeFilter, setOfficeFilter] = useState("All");
  const [missingCopyOnly, setMissingCopyOnly] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [fyPick, setFyPick] = useState("");
  const FY_OPTIONS = (() => {
    const n = new Date();
    const curFy = n.getMonth() + 1 >= 4 ? n.getFullYear() : n.getFullYear() - 1;
    return Array.from({ length: 5 }, (_, i) => curFy - i);
  })();
  const fyFrom = fyPick && fyPick !== "all" ? `${fyPick}-04-01` : "";
  const fyTo = fyPick && fyPick !== "all" ? `${Number(fyPick) + 1}-03-31` : "";
  const inFy = (r) => !fyFrom || ((r.issue_date || "") >= fyFrom && (r.issue_date || "") <= fyTo);
  const [selected, setSelected] = useState(() => new Set());
  const [editRow, setEditRow] = useState(null);
  const [editBillingAddr, setEditBillingAddr] = useState("");
  const [editDebitNote, setEditDebitNote] = useState(0);
  const [editDebitNoteReason, setEditDebitNoteReason] = useState("");
  const [editDate, setEditDate] = useState("");
  const [editUnloading, setEditUnloading] = useState(0);
  const [editHalting, setEditHalting] = useState(0);
  const [editLoading, setEditLoading] = useState(0);
  const [editLabour, setEditLabour] = useState(0);
  const [editLoadingHalting, setEditLoadingHalting] = useState(0);
  const [editCrane, setEditCrane] = useState(0);
  const [editVReturn, setEditVReturn] = useState(0);
  const [editEDelivery, setEditEDelivery] = useState(0);
  const [chargePresets, setChargePresets] = useState([]);
  const [outstanding, setOutstanding] = useState({});
  const [editDescription, setEditDescription] = useState("");
  const [editConsignor, setEditConsignor] = useState("");
  const [editConsignee, setEditConsignee] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [gstr1Open, setGstr1Open] = useState(false);
  const [collOpen, setCollOpen] = useState(false);
  const [tallyOpen, setTallyOpen] = useState(false);
  const [tallyForm, setTallyForm] = useState({ from: "", to: "", bills: true, receipts: true, notes: true });
  const [tallyBusy, setTallyBusy] = useState(false);
  const LEDGER_LABELS = { freight_income: "Freight Income ledger", cash: "Cash ledger", bank: "Bank ledger", tds: "TDS ledger", deduction: "Deduction ledger", note_recovery: "Credit Note recovery ledger" };
  const [ledgers, setLedgers] = useState(null);
  useEffect(() => {
    if (tallyOpen && !ledgers) api.get("/settings/tally-ledgers").then((r) => setLedgers(r.data)).catch(() => setLedgers({ freight_income: "Freight Income", cash: "Cash", bank: "Bank", tds: "TDS Receivable", deduction: "Deduction / Discount Allowed", note_recovery: "Deduction Recovered" }));
  }, [tallyOpen, ledgers]);
  const downloadTally = async () => {
    setTallyBusy(true);
    try {
      if (ledgers) await api.put("/settings/tally-ledgers", ledgers);
      const include = [tallyForm.bills && "bills", tallyForm.receipts && "receipts", tallyForm.notes && "notes"].filter(Boolean).join(",");
      const res = await api.get("/exports/tally", {
        params: { from_date: tallyForm.from, to_date: tallyForm.to, include },
        responseType: "blob",
      });
      const count = res.headers["x-voucher-count"];
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `ntc-tally-${tallyForm.from || "all"}-to-${tallyForm.to || "all"}.xml`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Tally XML downloaded${count ? ` — ${count} voucher(s)` : ""}`);
      setTallyOpen(false);
    } catch (e) {
      toast.error("Could not generate Tally export");
    } finally {
      setTallyBusy(false);
    }
  };

  const openEdit = (r) => {
    setEditRow(r);
    setEditDate(r.issue_date || "");
    setEditUnloading(r.unloading_charge || 0);
    setEditHalting(r.halting_charge || 0);
    setEditLoading(r.loading_charge || 0);
    setEditLabour(r.labour_union_charge || 0);
    setEditLoadingHalting(r.loading_point_halting_charge || 0);
    setEditCrane(r.crane_charge || 0);
    setEditVReturn(r.vehicle_return_charge || 0);
    setEditEDelivery(r.extra_delivery_charge || 0);
    setEditDescription(r.bill_description || "");
    setEditBillingAddr(r.billing_customer_address || "");
    setEditDebitNote(r.debit_note || 0);
    setEditDebitNoteReason(r.debit_note_reason || "");
    setEditConsignor(r.consignor || "");
    setEditConsignee(r.consignee || "");
  };

  const saveEdit = async () => {
    if (!editRow) return;
    setEditSaving(true);
    try {
      const res = await api.put(`/invoices/${editRow.id}`, {
        ...editRow,
        issue_date: editDate || editRow.issue_date,
        unloading_charge: Number(editUnloading) || 0,
        halting_charge: Number(editHalting) || 0,
        loading_charge: Number(editLoading) || 0,
        labour_union_charge: Number(editLabour) || 0,
        loading_point_halting_charge: Number(editLoadingHalting) || 0,
        crane_charge: Number(editCrane) || 0,
        vehicle_return_charge: Number(editVReturn) || 0,
        extra_delivery_charge: Number(editEDelivery) || 0,
        bill_description: editDescription,
        billing_customer_address: editBillingAddr,
        debit_note: Number(editDebitNote) || 0,
        debit_note_reason: editDebitNoteReason,
        consignor: editConsignor,
        consignee: editConsignee,
      });
      setRows((prev) => prev.map((x) => (x.id === editRow.id ? res.data : x)));
      const dnAmt = Number(editDebitNote) || 0;
      if (dnAmt > 0 && (dnAmt !== Number(editRow.debit_note || 0) || editDebitNoteReason !== (editRow.debit_note_reason || ""))) {
        if (res.data.debit_note_status === "Pending") {
          toast.warning(`Debit Note ${inr(dnAmt)} sent for admin approval`, {
            description: "It won't reduce the outstanding until the admin approves it. Alert the admin now?",
            duration: 15000,
            action: {
              label: "WhatsApp Admin",
              onClick: async () => {
                let phone = "";
                try { phone = (await api.get("/notes/admin-alert-contact")).data.phone || ""; } catch {}
                const d = String(phone).replace(/\D/g, "");
                const amsg = [
                  "*NTC — Note Approval Needed*",
                  "",
                  `Debit Note ${inr(dnAmt)} submitted on Bill ${String(res.data.invoice_no || "").replace(/^INV[-\s]?/i, "")}`,
                  `Customer: ${res.data.customer_name || "—"}`,
                  ...(editDebitNoteReason ? [`Reason: ${editDebitNoteReason}`] : []),
                  "",
                  "Please Approve/Reject in Outstanding → Approve Notes.",
                ].join("\n");
                window.open(d ? `https://wa.me/91${d.slice(-10)}?text=${encodeURIComponent(amsg)}` : `https://wa.me/?text=${encodeURIComponent(amsg)}`, "_blank");
              },
            },
          });
        } else {
        const c = custFor(res.data);
        const digits = String(c?.phone || "").replace(/\D/g, "");
        const msg = [
          "*NISHA TRANSPORT CORPORATION*",
          "*Debit Note Issued*",
          "",
          `Bill No: ${String(res.data.invoice_no || "").replace(/^INV[-\s]?/i, "")}`,
          `Customer: ${res.data.customer_name || "—"}`,
          `Debit Note Amount: ${inr(dnAmt)}`,
          ...(editDebitNoteReason ? [`Reason: ${editDebitNoteReason}`] : []),
          "",
          "This amount has been adjusted against the above bill.",
          "— Nisha Transport Corporation",
        ].join("\n");
        toast.info(`Debit Note ${inr(dnAmt)} recorded on bill ${res.data.invoice_no}`, {
          description: "Send the customer a WhatsApp confirmation?",
          duration: 12000,
          action: {
            label: "WhatsApp",
            onClick: () =>
              window.open(
                digits ? `https://wa.me/${digits.length === 10 ? "91" + digits : digits}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`,
                "_blank"
              ),
          },
        });
        }
      }
      toast.success(`Bill ${res.data.invoice_no} updated (${inr(res.data.total)})`, {
        description: "Do you want to print it now?",
        duration: 8000,
        action: { label: "Print Bill", onClick: () => printInvoice(res.data, custFor(res.data)) },
      });
      setEditRow(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setEditSaving(false);
    }
  };

  const toggleRow = (id) =>
    setSelected((p) => {
      const n = new Set(p);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const load = async () => {
    try {
      const [inv, cust] = await Promise.all([api.get("/invoices"), api.get("/customers")]);
      setRows(inv.data);
      setCustomers(cust.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => {
    load();
    api.get("/charge-presets").then((r) => setChargePresets(r.data || [])).catch(() => {});
    api.get("/reports/bill-outstanding").then((r) => {
      const map = {};
      (r.data || []).forEach((o) => { map[o.invoice_id] = o; });
      setOutstanding(map);
    }).catch(() => {});
  }, []);

  const custFor = (r) =>
    customers.find((c) => c.id === r.customer_id) || customers.find((c) => c.name === r.customer_name);

  const printBill = async (r) => {
    let dup = false;
    try {
      const res = await api.post(`/invoices/${r.id}/mark-printed`);
      const count = res.data.print_count || 1;
      dup = !isAdmin && count > 1;
      setRows((prev) => (prev || []).map((x) => (x.id === r.id ? { ...x, print_count: count, last_printed_at: new Date().toISOString() } : x)));
    } catch {
      dup = false;
    }
    printInvoice(r, custFor(r), { duplicate: dup });
    const c = custFor(r) || {};
    toast.info(`Bill ${String(r.invoice_no || "").replace(/^INV[-\s]?/i, "")} sent to print`, {
      description: `Use "Save as PDF" in the print window, then WhatsApp it to ${c.name || r.customer_name || "the customer"} with one tap.`,
      duration: 12000,
      action: { label: "WhatsApp Bill", onClick: () => waBill(r) },
    });
  };

  const waBill = (r) => {
    const c = custFor(r) || {};
    const digits = String(c.phone || "").replace(/\D/g, "");
    const lrLines = (r.items || [])
      .filter((it) => it.lr_no || it.from_place || it.destination)
      .map(
        (it, i) =>
          `${i + 1}. LR ${it.lr_no || "—"}${it.lr_date ? ` dt ${fmtDate(it.lr_date)}` : ""} · ${it.from_place || "—"} → ${it.destination || "—"}${it.packages ? ` · ${it.packages} pkg` : ""}${it.vehicle_no ? ` · ${it.vehicle_no}` : ""} — ₹${((Number(it.quantity) || 0) * (Number(it.rate) || 0)).toLocaleString("en-IN")}`
      );
    const msg = [
      "*Nisha Transport Corporation — Freight Bill*",
      "",
      `Bill No: ${String(r.invoice_no || "—").replace(/^INV[-\s]?/i, "")}`,
      `Bill Date: ${fmtDate(r.issue_date)}`,
      `Customer: ${r.customer_name || "—"}${c.customer_code ? ` (${c.customer_code})` : ""}`,
      "",
      ...(lrLines.length ? lrLines : [`Route: ${r.from_place || "—"} → ${r.destination || "—"}`]),
      "",
      `*Total Bill Amount: ${inr(r.total)}*`,
      r.status && r.status !== "Paid" ? `Status: ${r.status}` : "",
      "",
      "Kindly acknowledge and arrange the payment. Thank you!",
      "(Bill PDF attached — saved from the print window)",
      "— Nisha Transport Corporation",
    ].filter((l) => l !== "").join("\n");
    window.open(
      digits ? `https://wa.me/${digits.length === 10 ? "91" + digits : digits}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`,
      "_blank"
    );
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete bill ${r.invoice_no}? This cannot be undone.`)) return;
    try {
      await api.delete(`/invoices/${r.id}`);
      setRows((prev) => prev.filter((x) => x.id !== r.id));
      toast.success(`Bill ${r.invoice_no} deleted`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const stripInv = (v) => String(v || "").replace(/^INV[-\s]?/i, "") || "—";

  const printOutstanding = () => {
    const groups = buildOutstandingGroups();
    if (!groups.length) {
      toast.info("No pending bills — all payments received");
      return;
    }
    printOutstandingReport(
      groups,
      fyPick && fyPick !== "all"
        ? `FY ${fyPick}-${String((Number(fyPick) + 1) % 100).padStart(2, "0")} (as on ${fmtDate(new Date().toISOString().slice(0, 10))})`
        : fmtDate(new Date().toISOString().slice(0, 10))
    );
  };

  const downloadOutstandingCsv = () => {
    const groups = buildOutstandingGroups();
    if (!groups.length) {
      toast.info("No pending bills — all payments received");
      return;
    }
    const csvCell = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    };
    const lines = [["Customer", "Customer Address", "Bill No.", "Bill Date", "Status", "Bill Amount", "Received", "Balance Due"].join(",")];
    let gt = 0, gr = 0, gb = 0;
    groups.forEach((g) => {
      g.bills.forEach((b) => lines.push([g.customer, b.address || "", b.invoice_no, b.date, b.status, b.total, b.received, b.balance].map(csvCell).join(",")));
      lines.push([`${g.customer} SUB TOTAL`, "", "", "", "", g.total, g.received, g.balance].map(csvCell).join(","));
      gt += g.total; gr += g.received; gb += g.balance;
    });
    lines.push(["GRAND TOTAL", "", "", "", "", gt, gr, gb].map(csvCell).join(","));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `outstanding-report-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const sendCollectionWa = (g) => {
    const cust = customers.find((c) => c.name === g.customer);
    const phone = String(cust?.phone || "").replace(/\D/g, "");
    const msg = [
      "*Nisha Transport Corporation — Payment Reminder*",
      `Dear ${g.customer},`,
      "The following bill(s) are pending:",
      ...g.bills.map((b) => `• Bill ${b.invoice_no} (${b.date}) — Balance ${inr(b.balance)}`),
      `*Total due: ${inr(g.balance)}*`,
      "Kindly arrange the payment. Thank you.",
    ].join("\n");
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const buildOutstandingGroups = () => {
    const pending = (rows || [])
      .filter(inFy)
      .map((r) => ({ r, o: outstanding[r.id] }))
      .filter(({ r, o }) => (o ? o.balance > 0.01 : (r.status || "Unpaid") !== "Paid"));
    if (!pending.length) return [];
    const byCust = {};
    pending.forEach(({ r, o }) => {
      const name = r.customer_name || "—";
      const total = Number(r.total) || 0;
      const received = o ? o.received : 0;
      const balance = o ? o.balance : total;
      (byCust[name] = byCust[name] || []).push({
        invoice_no: stripInv(r.invoice_no),
        date: fmtDate(r.issue_date),
        status: o ? o.status : r.status || "Unpaid",
        address: r.billing_customer_address || "",
        total,
        received,
        balance,
        _d: r.issue_date || "",
      });
    });
    const groups = Object.keys(byCust)
      .sort((a, b) => a.localeCompare(b))
      .map((customer) => {
        const bills = byCust[customer].sort((a, b) => (a._d < b._d ? -1 : 1));
        return {
          customer,
          bills,
          total: bills.reduce((s, b) => s + b.total, 0),
          received: bills.reduce((s, b) => s + b.received, 0),
          balance: bills.reduce((s, b) => s + b.balance, 0),
        };
      });
    return groups;
  };
  const filtered = (rows || []).filter(
    (r) =>
      (!q ||
        (r.invoice_no || "").toLowerCase().includes(q.toLowerCase()) ||
        (r.customer_name || "").toLowerCase().includes(q.toLowerCase()) ||
        (custFor(r)?.customer_code || "").toLowerCase().includes(q.toLowerCase())) &&
      (officeFilter === "All" || (r.office || "Chennai") === officeFilter) &&
      (!missingCopyOnly || !r.bill_copy_path) &&
      (!dateFrom || (r.issue_date || "") >= dateFrom) &&
      (!dateTo || (r.issue_date || "") <= dateTo) &&
      inFy(r)
  );
  const totalBills = (rows || []).length;
  const missingCopyCount = (rows || []).filter((r) => !r.bill_copy_path).length;
  const billPages = Math.max(1, Math.ceil(filtered.length / 20));
  const curBillPage = Math.min(page, billPages);
  const pagedBills = filtered.slice((curBillPage - 1) * 20, curBillPage * 20);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); }, [q, officeFilter, missingCopyOnly, fyPick, dateFrom, dateTo]);
  const jumpToBill = () => {
    const jq = billJump.trim().toLowerCase().replace(/^inv[-\s]?/, "");
    if (!jq) return;
    const key = (r) => String(r.invoice_no || "").toLowerCase().replace(/^inv[-\s]?/, "");
    let idx = filtered.findIndex((r) => key(r) === jq);
    if (idx === -1) idx = filtered.findIndex((r) => key(r).includes(jq));
    if (idx === -1) {
      toast.error(`Bill "${billJump.trim()}" not found`);
      return;
    }
    const target = Math.floor(idx / 20) + 1;
    setPage(target);
    setBillHl(filtered[idx].id);
    toast.success(`Bill found on page ${target}`);
    setTimeout(() => setBillHl(null), 5000);
  };
  const allSelected = filtered.length > 0 && filtered.every((r) => selected.has(r.id));
  const toggleAll = () =>
    setSelected((p) => {
      const n = new Set(p);
      if (allSelected) filtered.forEach((r) => n.delete(r.id));
      else filtered.forEach((r) => n.add(r.id));
      return n;
    });
  const selectedRows = filtered.filter((r) => selected.has(r.id));
  const downloadBillsCsv = () => {
    const src = selectedRows.length ? selectedRows : filtered;
    if (!src.length) {
      toast.info("No bills to export");
      return;
    }
    const cell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [
      [`Bills Print — ${selectedRows.length ? `${selectedRows.length} selected` : `${src.length} bill(s)`}${dateFrom || dateTo ? ` · ${dateFrom ? fmtDate(dateFrom) : "…"} to ${dateTo ? fmtDate(dateTo) : "…"}` : ""}${officeFilter !== "All" ? ` · ${officeFilter}` : ""}`],
      ["Sl No", "Branch", "Customer Name", "Customer Code", "From", "Destination", "Bill No.", "Bill Date", "Bill Amount", "Received", "Balance", "Status"],
    ];
    let t = 0, tr = 0, tb = 0;
    src.forEach((r, i) => {
      const o = outstanding[r.id];
      const total = Number(r.total) || 0;
      const received = o ? Number(o.received) || 0 : (r.status === "Paid" ? total : 0);
      const balance = o ? Number(o.balance) || 0 : total - received;
      t += total; tr += received; tb += balance;
      lines.push([i + 1, r.office || "Chennai", r.customer_name || "", custFor(r)?.customer_code || "", r.from_place || "", r.destination || "", stripInv(r.invoice_no), r.issue_date ? fmtDate(r.issue_date) : "", total, received, balance, o ? o.status : r.status || "Unpaid"]);
    });
    lines.push(["", "", "", "", "", "", "", "TOTAL", t, tr, tb, ""]);
    const blob = new Blob(["\ufeff" + lines.map((l) => l.map(cell).join(",")).join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `bills-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };
  const printSelected = () => {
    if (selectedRows.length === 0) {
      toast.info("Select at least one bill to print");
      return;
    }
    printInvoices(selectedRows, custFor);
  };

  return (
    <div>
      <PageHeader title="Bills Print" subtitle="All created freight bills — reprint any bill anytime in the official Tax Invoice format." />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search by bill no. or customer" value={q} onChange={(e) => setQ(e.target.value)} data-testid="bills-search" />
        </div>
        <div className="flex items-center gap-1.5">
          <Input
            className="w-[140px]"
            placeholder="Jump to Bill No."
            value={billJump}
            onChange={(e) => setBillJump(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && jumpToBill()}
            data-testid="bill-jump-input"
          />
          <Button variant="outline" size="icon" onClick={jumpToBill} title="Jump to Bill" data-testid="bill-jump-btn">
            <Search className="h-4 w-4" />
          </Button>
        </div>
        {isAdmin && (
          <Select value={officeFilter} onValueChange={setOfficeFilter}>
            <SelectTrigger className="w-[170px]" data-testid="bills-office-filter">
              <Building2 className="mr-1 h-4 w-4 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All Branches</SelectItem>
              {OFFICES.map((o) => (
                <SelectItem key={o} value={o}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select
          value={fyPick}
          onValueChange={setFyPick}
        >
          <SelectTrigger className="w-[150px]" data-testid="bills-fy-filter">
            <SelectValue placeholder="FY (Apr–Mar)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All dates</SelectItem>
            {FY_OPTIONS.map((y) => (
              <SelectItem key={y} value={String(y)}>
                FY {y}-{String((y + 1) % 100).padStart(2, "0")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1 rounded-md border border-input bg-white px-2 py-1">
          <span className="text-xs text-slate-500">From</span>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-7 w-[135px] border-0 px-1 text-xs shadow-none" data-testid="bills-date-from" />
          <span className="text-xs text-slate-500">To</span>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-7 w-[135px] border-0 px-1 text-xs shadow-none" data-testid="bills-date-to" />
          {(dateFrom || dateTo) && (
            <button type="button" className="text-slate-400 hover:text-slate-600" title="Clear date range" onClick={() => { setDateFrom(""); setDateTo(""); }} data-testid="bills-date-clear">✕</button>
          )}
        </div>
        <div className="flex items-center gap-2 rounded-md border border-border bg-slate-50 px-3 py-2" data-testid="bills-missing-copy-toggle">
          <Switch checked={missingCopyOnly} onCheckedChange={setMissingCopyOnly} data-testid="bills-missing-copy-only" />
          <Label className="text-xs font-medium text-slate-600">Missing copy only</Label>
        </div>
        {totalBills > 0 && (
          <span
            className={`rounded-full px-2.5 py-1 text-xs font-semibold ${missingCopyCount ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}
            data-testid="bills-copy-count-badge"
          >
            {missingCopyCount ? `${missingCopyCount} of ${totalBills} bills missing copy` : `All ${totalBills} bills have a copy`}
          </span>
        )}
        <Button variant="outline" size="sm" className="ml-auto gap-1.5" onClick={downloadBillsCsv} data-testid="bills-excel-btn">
          <FileSpreadsheet className="h-4 w-4" /> Excel{selectedRows.length ? ` (${selectedRows.length})` : ""}
        </Button>
        <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setGstr1Open(true)} data-testid="gstr1-btn">
          <FileSpreadsheet className="h-4 w-4" /> GSTR-1
        </Button>
        {isAdmin && (
          <Button variant="outline" size="sm" className="gap-1.5" onClick={printOutstanding} data-testid="outstanding-report-btn">
            <Printer className="h-4 w-4" /> Outstanding
          </Button>
        )}
        {isAdmin && (
          <Button variant="outline" size="sm" className="gap-1.5" onClick={downloadOutstandingCsv} data-testid="outstanding-excel-btn">
            <FileSpreadsheet className="h-4 w-4" /> Outstanding Excel
          </Button>
        )}
        {isAdmin && (
          <Button variant="outline" size="sm" className="gap-1.5 border-blue-300 text-blue-700 hover:bg-blue-50" onClick={() => setTallyOpen(true)} data-testid="tally-export-btn">
            <FileSpreadsheet className="h-4 w-4" /> Tally Export
          </Button>
        )}
        <Button variant="outline" size="sm" className="gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => setCollOpen(true)} data-testid="collections-btn">
          <MessageCircle className="h-4 w-4" /> Collections
        </Button>
        <Button variant="secondary" size="sm" className="gap-1.5" onClick={printSelected} disabled={selectedRows.length === 0} data-testid="print-selected-bills-btn">
          <Printer className="h-4 w-4" /> Print Selected{selectedRows.length ? ` (${selectedRows.length})` : ""}
        </Button>
      </div>

      <Dialog open={tallyOpen} onOpenChange={setTallyOpen}>
        <DialogContent className="sm:max-w-[420px]" data-testid="tally-export-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Tally Export (XML)</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            Downloads Bills as Sales vouchers, Money Receipts as Receipt vouchers and Credit/Debit Notes as Journal vouchers. Import in Tally via Gateway of Tally → Import Data → Vouchers.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>From date</Label>
              <Input type="date" value={tallyForm.from} onChange={(e) => setTallyForm((f) => ({ ...f, from: e.target.value }))} data-testid="tally-from" />
            </div>
            <div className="grid gap-1.5">
              <Label>To date</Label>
              <Input type="date" value={tallyForm.to} onChange={(e) => setTallyForm((f) => ({ ...f, to: e.target.value }))} data-testid="tally-to" />
            </div>
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={tallyForm.bills} onCheckedChange={(v) => setTallyForm((f) => ({ ...f, bills: !!v }))} data-testid="tally-include-bills" /> Bills (Sales)
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={tallyForm.receipts} onCheckedChange={(v) => setTallyForm((f) => ({ ...f, receipts: !!v }))} data-testid="tally-include-receipts" /> Receipts
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={tallyForm.notes} onCheckedChange={(v) => setTallyForm((f) => ({ ...f, notes: !!v }))} data-testid="tally-include-notes" /> CN/DN Notes
            </label>
          </div>
          <div className="rounded-md border bg-slate-50 p-3">
            <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-500">Ledger names — match your Tally books exactly</div>
            <div className="grid grid-cols-2 gap-2.5">
              {ledgers &&
                Object.entries(LEDGER_LABELS).map(([k, lbl]) => (
                  <div key={k} className="grid gap-1">
                    <Label className="text-[11px] text-slate-600">{lbl}</Label>
                    <Input
                      className="h-8 text-xs"
                      value={ledgers[k] || ""}
                      onChange={(e) => setLedgers((m) => ({ ...m, [k]: e.target.value }))}
                      data-testid={`tally-ledger-${k}`}
                    />
                  </div>
                ))}
              {!ledgers && <span className="text-xs text-muted-foreground">Loading ledger names…</span>}
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">Saved automatically on download — future exports remember these names.</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setTallyOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={downloadTally} disabled={tallyBusy || (!tallyForm.bills && !tallyForm.receipts && !tallyForm.notes)} data-testid="tally-download-btn">
              {tallyBusy ? "Preparing…" : "Download XML"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <TableShell>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="w-10">
                <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="select-all-bills-list" aria-label="Select all bills" />
              </TableHead>
              {["SL No.", "Branch", "Customer Name", "Customer Code", "From", "Destination", "Bill No.", "Bill Date", "Bill Amount", "Payment Status", "Prints"].map((h) => (
                <TableHead key={h} className={`text-[11px] uppercase tracking-[0.15em] text-slate-500 ${h === "Bill Amount" ? "text-right" : ""} ${h === "Prints" ? "text-center" : ""}`}>{h}</TableHead>
              ))}
              <TableHead className="w-[110px] text-right text-[11px] uppercase tracking-[0.15em] text-slate-500">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows === null &&
              [1, 2, 3].map((i) => (
                <TableRow key={i}>
                  {Array.from({ length: 10 }).map((_, j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-20" /></TableCell>
                  ))}
                </TableRow>
              ))}
            {rows && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={12} className="py-12 text-center text-muted-foreground">No bills found.</TableCell>
              </TableRow>
            )}
            {pagedBills.map((r, pi) => (
              <TableRow key={r.id} data-testid="bill-row" className={billHl === r.id ? "bg-amber-100 hover:bg-amber-100" : ""} data-state={selected.has(r.id) ? "selected" : undefined}>
                <TableCell className="w-10">
                  <Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggleRow(r.id)} data-testid="select-bill-list" aria-label="Select bill" />
                </TableCell>
                <TableCell className="text-sm" data-testid="bill-sl-cell">{(curBillPage - 1) * 20 + pi + 1}</TableCell>
                <TableCell className="text-sm font-medium" data-testid="bill-branch-cell">{r.office || "Chennai"}</TableCell>
                <TableCell className="text-sm">{r.customer_name || "—"}</TableCell>
                <TableCell className="text-sm" data-testid="bill-customer-code-cell">{custFor(r)?.customer_code || "—"}</TableCell>
                <TableCell className="text-sm" data-testid="bill-from-cell">{r.from_place || "—"}</TableCell>
                <TableCell className="text-sm" data-testid="bill-destination-cell">{r.destination || "—"}</TableCell>
                <TableCell className="font-medium">
                  <span className="inline-flex items-center gap-1.5">
                    {stripInv(r.invoice_no)}
                    <NewBadge createdAt={r.created_at} />
                    {r.bill_copy_path && (
                      <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700" title="Bill copy attached" data-testid="bill-copy-tick">
                        <Paperclip className="h-2.5 w-2.5" /> copy
                      </span>
                    )}
                  </span>
                </TableCell>
                <TableCell className="text-sm">{fmtDate(r.issue_date)}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{inr(r.total)}</TableCell>
                <TableCell className="text-sm">
                  <span className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-medium ${r.status === "Paid" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : r.status === "Partly Paid" ? "border-sky-200 bg-sky-50 text-sky-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>
                    {r.status || "Unpaid"}
                  </span>
                  {r.status === "Partly Paid" && outstanding[r.id] ? (
                    <div className="mt-0.5 text-[11px] font-semibold text-red-600" data-testid="bill-pending-amount">
                      {inr(outstanding[r.id].balance)} pending
                    </div>
                  ) : null}
                </TableCell>
                <TableCell className="text-center text-sm" data-testid="bill-print-count">
                  {Number(r.print_count) > 0 ? (
                    <span className="inline-block min-w-[26px] rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-700" title={r.last_printed_at ? `Last printed ${fmtDate(r.last_printed_at.slice(0, 10))}` : undefined}>
                      {r.print_count}
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {Number(r.debit_note) > 0 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-600 hover:text-red-700"
                        title={`Print Debit Note voucher (${inr(r.debit_note)})`}
                        onClick={() => printNoteVoucher({ date: r.issue_date, party: r.customer_name, ref: r.invoice_no, amount: r.debit_note, reason: r.debit_note_reason, branch: r.office }, "debit")}
                        data-testid="print-dn-voucher-btn"
                      >
                        <StickyNote className="h-4 w-4" />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-600 hover:text-emerald-700" title="WhatsApp bill summary" onClick={() => waBill(r)} data-testid="bill-wa-btn">
                      <MessageCircle className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-primary" title="Print bill" onClick={() => printBill(r)} data-testid="print-bill-btn">
                      <Printer className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" title="Edit charges" onClick={() => openEdit(r)} data-testid="edit-bill-charges-btn">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" title="Delete bill" onClick={() => remove(r)} data-testid="delete-bill-btn">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableShell>
      <ListPagination total={filtered.length} page={page} setPage={setPage} noun="bills" testPrefix="bills" />

      <Gstr1Dialog open={gstr1Open} onClose={() => setGstr1Open(false)} rows={rows || []} custFor={custFor} onChanged={load} />

      <Dialog open={collOpen} onOpenChange={(v) => !v && setCollOpen(false)}>
        <DialogContent className="max-w-lg" data-testid="collections-dialog">
          <DialogHeader>
            <DialogTitle>Collection Reminders</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] space-y-2 overflow-y-auto">
            {buildOutstandingGroups().length === 0 && (
              <p className="text-sm text-muted-foreground">No pending bills — all payments received.</p>
            )}
            {buildOutstandingGroups().map((g) => {
              const cust = customers.find((c) => c.name === g.customer);
              return (
                <div key={g.customer} className="flex items-center gap-3 rounded-md border border-border px-3 py-2" data-testid="collection-row">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold">{g.customer}</div>
                    <div className="text-xs text-muted-foreground">
                      {g.bills.length} pending bill(s) · <span className="font-semibold text-red-600">{inr(g.balance)} due</span>
                      {!cust?.phone && <span className="ml-1 text-amber-600">· no mobile saved</span>}
                    </div>
                  </div>
                  <Button size="sm" className="ml-auto h-8 shrink-0 bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => sendCollectionWa(g)} data-testid={`collection-wa-btn`}>
                    <MessageCircle className="mr-1.5 h-3.5 w-3.5" /> WhatsApp
                  </Button>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editRow} onOpenChange={(o) => !o && setEditRow(null)}>
        <DialogContent className="sm:max-w-[420px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Edit Charges — {editRow ? stripInv(editRow.invoice_no) : ""}</DialogTitle>
            {editRow?.edit_log?.length ? (
              <p className="text-[11px] text-muted-foreground" data-testid="bill-edit-history">
                Update history: {editRow.edit_log.slice(-3).map((e) => `${e.by || "—"} on ${fmtDate(String(e.at || "").slice(0, 10))}`).join(" · ")}
              </p>
            ) : null}
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5">
              <Label>Bill Date (change for back-dated bill)</Label>
              <Input type="date" value={editDate} onChange={(e) => setEditDate(e.target.value)} data-testid="edit-bill-date" />
            </div>
            <div className="grid gap-1.5">
              <Label>Consignor Name</Label>
              <Input value={editConsignor} onChange={(e) => setEditConsignor(e.target.value)} data-testid="edit-consignor" />
            </div>
            <div className="grid gap-1.5">
              <Label>Consignee Name (use "A & B" for multiple)</Label>
              <Input value={editConsignee} onChange={(e) => setEditConsignee(e.target.value)} data-testid="edit-consignee" />
            </div>
            <div className="grid gap-1.5">
              <Label>Loading Charge (₹)</Label>
              <Input type="number" value={editLoading} onChange={(e) => setEditLoading(e.target.value)} data-testid="edit-loading-charge" />
              <PresetChips presets={chargePresets} type="loading" onPick={setEditLoading} />
            </div>
            <div className="grid gap-1.5">
              <Label>Unloading Charge (₹)</Label>
              <Input type="number" value={editUnloading} onChange={(e) => setEditUnloading(e.target.value)} data-testid="edit-unloading-charge" />
              <PresetChips presets={chargePresets} type="unloading" onPick={setEditUnloading} />
            </div>
            <div className="grid gap-1.5">
              <Label>Loading Point Halting Charge (₹)</Label>
              <Input type="number" value={editLoadingHalting} onChange={(e) => setEditLoadingHalting(e.target.value)} data-testid="edit-loading-point-halting-charge" />
              <PresetChips presets={chargePresets} type="lph" onPick={setEditLoadingHalting} />
            </div>
            <div className="grid gap-1.5">
              <Label>Unloading Point Halting Charge (₹)</Label>
              <Input type="number" value={editHalting} onChange={(e) => setEditHalting(e.target.value)} data-testid="edit-halting-charge" />
              <PresetChips presets={chargePresets} type="halting" onPick={setEditHalting} />
            </div>
            <div className="grid gap-1.5">
              <Label>Labour Union Charge (₹)</Label>
              <Input type="number" value={editLabour} onChange={(e) => setEditLabour(e.target.value)} data-testid="edit-labour-union-charge" />
              <PresetChips presets={chargePresets} type="labour" onPick={setEditLabour} />
            </div>
            <div className="grid gap-1.5">
              <Label>Crane Charge (₹)</Label>
              <Input type="number" value={editCrane} onChange={(e) => setEditCrane(e.target.value)} data-testid="edit-crane-charge" />
              <PresetChips presets={chargePresets} type="crane" onPick={setEditCrane} />
            </div>
            <div className="grid gap-1.5">
              <Label>Vehicle Return Charge (₹)</Label>
              <Input type="number" value={editVReturn} onChange={(e) => setEditVReturn(e.target.value)} data-testid="edit-vehicle-return-charge" />
              <PresetChips presets={chargePresets} type="vreturn" onPick={setEditVReturn} />
            </div>
            <div className="grid gap-1.5">
              <Label>Extra Delivery Charge (₹)</Label>
              <Input type="number" value={editEDelivery} onChange={(e) => setEditEDelivery(e.target.value)} data-testid="edit-extra-delivery-charge" />
              <PresetChips presets={chargePresets} type="edelivery" onPick={setEditEDelivery} />
            </div>
            <div className="grid gap-1.5">
              <Label>Description (prints above items table)</Label>
              <Textarea value={editDescription} onChange={(e) => setEditDescription(e.target.value)} data-testid="edit-bill-description" rows={2} />
            </div>
            <div className="grid gap-1.5">
              <Label>Billing Customer Address (shown in Outstanding Report)</Label>
              <Input value={editBillingAddr} onChange={(e) => setEditBillingAddr(e.target.value)} placeholder="e.g. Sricity-517646" data-testid="edit-billing-customer-address" />
            </div>
            <div className="grid gap-1.5">
              <Label>Debit Note — Our Bill Deduction (₹)</Label>
              <Input type="number" value={editDebitNote} onChange={(e) => setEditDebitNote(e.target.value)} placeholder="0" data-testid="edit-debit-note" />
              <span className="text-[11px] text-muted-foreground">Reduces this bill's balance in the Outstanding Report</span>
            </div>
            <div className="grid gap-1.5">
              <Label>Debit Note Reason</Label>
              <Input value={editDebitNoteReason} onChange={(e) => setEditDebitNoteReason(e.target.value)} placeholder="e.g. Shortage / Damage / Rate difference" data-testid="edit-debit-note-reason" />
            </div>
            <p className="text-xs text-muted-foreground">The bill total (and GST, if applicable) will be recalculated automatically.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditRow(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={editSaving} data-testid="save-bill-charges-btn">
              {editSaving ? "Saving…" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
